import {
  effect,
  Injectable,
  signal,
  ApplicationRef,
  ComponentRef,
  Injector,
  createComponent,
} from "@angular/core";
import { SuggestionProps } from "@tiptap/suggestion";

import { ProjectProvider } from "src/app/providers";
import { Mentionable } from "src/app/models";
import { SuggestionOption } from "../models";

import { MentionsPopoverComponent } from "../ui/mentions-popover/mentions-popover.component";

@Injectable({
  providedIn: "root",
})
export class SuggestionsService {
  elementRef: Element | null = null;
  /** All possible suggestions */
  #items: Mentionable[] = [];
  /** filtered suggestions */
  private filteredItems = signal<SuggestionOption[]>([]);
  /** reference to active popover */
  private activePopover: ComponentRef<MentionsPopoverComponent> | null = null;
  /** @ignore */
  constructor(
    private pp: ProjectProvider,
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    effect(() => {
      const items = this.filteredItems();
      if (this.activePopover) {
        this.activePopover.instance.suggestions.set(items);
      }
    });
  }

  /**Load the mentionables */
  init(): void {
    this.#items = [...this.pp.mentions];
  }

  /** Set the target element for the popover */
  onStart(props: SuggestionProps, elementRef?: Element | null): void {
    this.filteredItems.set(
      props.items.map((item) => {
        return {
          command: () => props.command(item),
          item: item,
        };
      })
    );

    if (elementRef) {
      this.elementRef = elementRef;
      this.showPopoverAt(this.elementRef);
    }
  }

  /** Run the onUpdate function and display popover if search has results */
  onUpdate(props: SuggestionProps): void {
    if (!props.clientRect) {
      return;
    }
    this.filteredItems.set(
      props.items.map((item) => {
        return {
          command: () => props.command(item),
          item: item,
        };
      })
    );
  }

  /** Clear the search */
  clear(): void {
    this.destroyActivePopover();
    this.filteredItems.set([]);
    this.elementRef = null;
  }

  search(query: string): Mentionable[] {
    let result = [...this.#items].filter(
      (item: { userId: any; email: string; name: string }) => {
        const labelNormalized = item.name.toLowerCase().trim();
        const queryNormalized = query.toLowerCase().trim();
        return labelNormalized.includes(queryNormalized);
      }
    );
    return result;
  }

  private showPopoverAt(anchorElement: Element): void {
    // destroy if there's an open one
    this.destroyActivePopover();

    const rect = anchorElement.getBoundingClientRect();

    this.activePopover = createComponent(MentionsPopoverComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });

    this.activePopover.instance.top.set(rect.top + window.scrollY);
    this.activePopover.instance.left.set(rect.left + window.scrollX);

    // Attach to DOM
    this.appRef.attachView(this.activePopover.hostView);
    document.body.appendChild(
      (this.activePopover.hostView as any).rootNodes[0]
    );
  }

  /** Remove popover from dom and remove reference */
  private destroyActivePopover(): void {
    if (this.activePopover) {
      this.appRef.detachView(this.activePopover.hostView);
      this.activePopover.destroy();
      this.activePopover = null;
    }
  }
}
